import { show } from '../../core/alerts';
import { alertText as say } from '../../core/copy';

/**
 * Everything a chapter or an event can say about a request to join it.
 *
 * This is the door with a real approval axis: a person decides, so a request can be
 * waiting, approved or turned down, and most of these sentences exist because one
 * of those is true. RegisterController and RegistrationApprovalService set the
 * status; this reads it and nothing more.
 *
 * Both entries use this file. A guest reaches all of it; a member signing in
 * reaches only signedIn, because the route they post to answers with nothing else.
 *
 * Sentences about a step rather than about this door — a wrong code, a refused
 * sign-in, a failed post — are in core/alerts and are the same on every door.
 */

/** The word on the button before the address is looked up. */
export const label = 'register';

/** They are in and the chapter has them. */
function allSet(name, onContinue) {
    show({
        title: say('all_set', 'All set, :userName!').replace(':userName', name || ''),
        text: say('thanks_for_registering', 'Thanks for registering!'),
        icon: 'success',
        confirmButtonText: say('continue', 'Continue'),
        onConfirm: onContinue,
        whenMissing: onContinue
    });
}

function waiting(titleKey, titleFallback, textKey, textFallback, onContinue) {
    show({
        title: say(titleKey, titleFallback),
        text: say(textKey, textFallback),
        icon: 'success',
        confirmButtonText: onContinue ? say('approve_pending', 'OK') : say('continue', 'Continue'),
        onConfirm: onContinue,
        whenMissing: onContinue
    });
}

/**
 * The request was accepted. An event can come back already approved; a chapter
 * cannot, and answers pending or pending_verification.
 */
export function registered(response) {
    if (response.status === 'approved') {
        waiting(
            'registration_approved_verify_title', 'Registration Approved!',
            'registration_approved_verify_text', 'You can verify your email now or later.'
        );

        return;
    }

    if (response.status === 'pending_verification') {
        confirmFirst();

        return;
    }

    waiting(
        'registration_pending_verify_title', 'Registration Pending!',
        'registration_pending_verify_text', 'Your request has been sent successfully.'
    );
}

/**
 * A nonpublic request waits on the address before anybody reviews it, so this path
 * offers no "later" — the skip button is gone on it too.
 */
export function confirmFirst() {
    show({
        title: say('awaiting_verification_title', 'Almost there'),
        text: say('awaiting_verification_text', 'Enter the code we just emailed you to complete your registration.'),
        icon: 'info',
        confirmButtonText: say('continue', 'Continue')
    });
}

/**
 * Signing in worked and there was nothing to file: a membership already granted, a
 * request already waiting, or a chapter that takes no members. Which one it was
 * comes back as the status, from whichever endpoint the door posted to.
 */
const NOTHING_TO_FILE = {
    already_member: ['already_member_text', 'You are already a member of this Chapter.'],
    already_requested: ['already_requested_text', 'Your request to join this Chapter is already under review.'],
    not_open: ['chapter_not_open_text', 'This Chapter is not accepting membership requests.']
};

function nothingToFile(status, onContinue) {
    show({
        title: say('nothing_to_submit', 'Nothing to submit'),
        text: say(...NOTHING_TO_FILE[status]),
        icon: 'info',
        confirmButtonText: say('close', 'Close'),
        onConfirm: onContinue,
        whenMissing: onContinue
    });
}

export function signedIn({ approved, name, status, onContinue }) {
    if (NOTHING_TO_FILE[status]) {
        nothingToFile(status, onContinue);

        return;
    }

    if (approved) {
        allSet(name, onContinue);

        return;
    }

    waiting(
        'registration_pending', 'Registration pending',
        'registration_pending_text', 'Your registration is pending approval.',
        onContinue
    );
}

/** The same news, reached by confirming the address rather than by signing in. */
export function addressConfirmed({ approved, name, onContinue }) {
    if (approved) {
        allSet(name, onContinue);

        return;
    }

    waiting(
        'registration_pending_code', 'Registration pending',
        'registration_pending_text_code', 'Your registration is pending approval.',
        onContinue
    );
}

/** A chair turned the request down. */
export function rejected({ onConfirm } = {}) {
    show({
        title: say('registration_rejected', 'Registration Update'),
        html: say(
            'registration_rejected_text',
            'Your registration was rejected. If you believe this is a mistake, please contact <a href="mailto:support@LegalOpsKPIs.com">support@LegalOpsKPIs.com</a>.'
        ),
        icon: 'info',
        confirmButtonText: say('close', 'Close'),
        onConfirm,
        whenMissing: onConfirm
    });
}

/** The seat is behind a plan, which is a refusal with a way out rather than a no. */
export function planRequired({ upgradeUrl, onClose } = {}) {
    if (typeof Swal === 'undefined') {
        if (upgradeUrl) {
            window.open(upgradeUrl, '_blank');
        }

        return;
    }

    Swal.fire({
        title: say('upgrade_required_title', 'Upgrade Required'),
        text: say('upgrade_required_message', 'Your first event is free. Upgrade to register for more events.'),
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: say('upgrade_now', 'Upgrade Now'),
        cancelButtonText: say('close', 'Close')
    }).then((result) => {
        if (result.isConfirmed && upgradeUrl) {
            window.location.href = upgradeUrl;

            return;
        }

        if (result.dismiss && typeof onClose === 'function') {
            onClose();
        }
    });
}
